import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import Shell from '../dashboard/Shell';
import Modal from '../../components/ui/Modal';
import axiosInstance from '../../axiosConfig';
import { Check, Sparkles, Loader2 } from 'lucide-react';
import './UpgradePage.css';

// Spike flag. When true → tier selection navigates to /upgrade/checkout
// (route-based summary page). When false → the old proration modal shows.
// Flip to `false` here to revert instantly with no other code change.
// Long-term: promote to PlatformSettings / feature-flag service.
export const UPGRADE_CHECKOUT_V2 = true;

const PERIODS = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY',  label: 'Yearly' },
];

const LOCALE_MAP = {
  NGN: 'en-NG', USD: 'en-US', GBP: 'en-GB', EUR: 'de-DE',
  GHS: 'en-GH', KES: 'en-KE', ZAR: 'en-ZA',
};

// Curated per-tier highlights for the cards. Client-side rather than
// backend-served so we can position each tier distinctly — the backend's
// tier.features array is derived from access grants, not marketing copy.
// Any admin-created tier not in this map falls back to the top items from
// the backend's tier.features so its card still reads as something.
// Tier seniority (by price): PRO_PLUS (₦55k) > PRO (₦15k) > PREMIUM (₦7.5k) > FREE.
// Copy is grounded in the actual admin-configured tier limits and
// positions each card as a step up from the one below it — the row reads
// as a progression rather than overlapping selling points. Custom domain
// is available from PREMIUM upwards, not exclusive to the top tier.
const PLAN_HIGHLIGHTS = {
  FREE: [
    '25 products, 2 locations',
    '2 team members',
    '500 MB media storage',
    'Community support',
  ],
  PREMIUM: [
    '60 products, 5 locations',
    '8 team members, 5 GB storage',
    'Custom domain + branded storefront',
    'Email support',
  ],
  PRO: [
    'Everything in Premium',
    '100 products, 10 locations',
    '15 team members, 10 GB storage',
    'POS terminal + WhatsApp AI assistant',
  ],
  PRO_PLUS: [
    'Everything in Pro',
    'Unlimited products & variants',
    '20 locations, 30 team members, 30 GB storage',
    'AI forecasts + priority SLA support',
  ],
};

// Normalise tier keys so admin-entered "Pro Plus" / "pro-plus" / "PRO_PLUS"
// all resolve to the same PLAN_HIGHLIGHTS bucket.
const normalizeTierKey = (raw) => String(raw || '').toUpperCase().replace(/[\s-]+/g, '_');

// Badge shown at the top of each card. Only ONE card carries "Most popular"
// (PREMIUM — the entry-paid tier and the highest-volume conversion point);
// the others get distinct positioning so the row reads as a progression:
//
//   FREE (no badge) → PREMIUM (Most popular) → PRO (Scale) → PRO_PLUS (Best value)
//
// Seniority by price: PRO_PLUS (₦50k) > PRO (₦15k) > PREMIUM (₦7.5k) > FREE.
const getPlanBadge = (tierKey, isCurrent) => {
  if (isCurrent) return { text: 'Your plan', variant: 'current' };
  if (tierKey === 'PREMIUM')  return { text: 'Most popular', variant: 'popular' };
  if (tierKey === 'PRO')      return { text: 'Scale', variant: 'scale' };
  if (tierKey === 'PRO_PLUS') return { text: 'Best value', variant: 'best' };
  return null;
};

export default function UpgradePage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useSecureAuth();

  const [tiers, setTiers] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('MONTHLY');
  const [merchantCurrency, setMerchantCurrency] = useState('NGN');

  const [showProrationModal, setShowProrationModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [proratedAmount, setProratedAmount] = useState(null);
  const [calculatingProration, setCalculatingProration] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [processingTier] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tiersResponse, subscriptionResponse, profileResponse] = await Promise.all([
        fetch('/api/tiers/merchant/available', { credentials: 'include' }),
        axiosInstance.get('/merchant/billing/subscription').catch(() => null),
        axiosInstance.get('/auth/profile').catch(() => null),
      ]);

      const currency = profileResponse?.data?.currency || 'NGN';
      if (profileResponse?.data?.currency) setMerchantCurrency(profileResponse.data.currency);

      if (tiersResponse.ok) {
        const tiersData = await tiersResponse.json();
        const tiersList = tiersData.message || tiersData || [];
        const normalized = Array.isArray(tiersList) ? tiersList.map((tier) => ({
          ...tier,
          tier: tier.tier?.toUpperCase() || tier.tier,
          monthlyPrice: tier.price || tier.monthlyPrice || 0,
          yearlyPrice: tier.yearlyPrice || (tier.price ? tier.price * 10 : 0),
          pricing: tier.pricing || {
            [currency]: { monthly: tier.price || 0, yearly: tier.yearlyPrice || 0 },
          },
        })) : [];
        setTiers(normalized);
      }

      if (subscriptionResponse?.data?.success && subscriptionResponse.data.subscription) {
        const subData = subscriptionResponse.data.subscription;
        setCurrentSubscription({
          tier: subData.currentTier,
          status: subData.activeSubscription?.status || 'ACTIVE',
          period: subData.activeSubscription?.period || 'MONTHLY',
          currentPeriodEnd: subData.expiresAt,
          amount: subData.tierDetails?.price || 0,
          ...subData,
        });
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const currentTier = currentSubscription?.tier || user?.merchantTier || 'FREE';
  const currentTierData = tiers.find((t) => t.tier?.toUpperCase() === currentTier?.toUpperCase());
  const currentTierSortOrder =
    currentSubscription?.currentTierSortOrder ?? currentTierData?.sortOrder ?? 0;

  const isCurrentPlan = (tierName) => tierName?.toUpperCase() === currentTier?.toUpperCase();
  const isDowngradeTier = (tier) =>
    tier?.isDowngrade !== undefined ? tier.isDowngrade : (tier?.sortOrder ?? 0) < currentTierSortOrder;
  const isUpgradeTier = (tier) =>
    tier?.isUpgrade !== undefined ? tier.isUpgrade : (tier?.sortOrder ?? 0) > currentTierSortOrder;

  const getPrice = (tier, period) => {
    if (tier.tier === 'FREE') return 0;
    const currencyPricing = (tier.pricing || {})[merchantCurrency]
      || (tier.pricing || {}).NGN || {};
    return period === 'YEARLY'
      ? currencyPricing.yearly || tier.yearlyPrice || 0
      : currencyPricing.monthly || tier.monthlyPrice || 0;
  };

  const formatPrice = (price, currency = merchantCurrency) => {
    const locale = LOCALE_MAP[currency] || 'en-NG';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getButtonText = (tier) => {
    if (processingTier === tier.tier) return 'Processing…';
    if (isCurrentPlan(tier.tier)) return 'Current plan';
    if (isDowngradeTier(tier)) return 'Downgrade';
    if (isUpgradeTier(tier)) return 'Upgrade';
    return 'Select';
  };

  const handleSelectTier = async (tier) => {
    if (!tier) return;
    const isUpgrade = (tier.sortOrder ?? 0) > currentTierSortOrder;
    const action = isUpgrade ? 'upgrade' : 'downgrade';

    // V2 flow — deep-linkable checkout page. Refetches proration itself
    // so we don't need to prewarm state here. Modal is bypassed entirely.
    if (UPGRADE_CHECKOUT_V2) {
      const params = new URLSearchParams({
        tier: tier.tier,
        action,
        name: tier.name || tier.tier,
      });
      navigate(`/upgrade/checkout?${params.toString()}`);
      return;
    }

    // Legacy modal flow — kept while V2 bakes on staging.
    setSelectedTier(tier);
    setShowProrationModal(true);
    setCalculatingProration(true);
    setProratedAmount(null);
    try {
      const response = await axiosInstance.post('/merchant/billing/calculate-proration', {
        newTier: tier.tier,
        action,
      });
      if (response.data?.success) {
        setProratedAmount(response.data);
      } else {
        throw new Error(response.data?.error || 'Failed to calculate pricing');
      }
    } catch (err) {
      console.error('Error calculating proration:', err);
      toast.error('Failed to calculate pricing: ' + (err.response?.data?.error || err.message));
      setShowProrationModal(false);
      setSelectedTier(null);
    } finally {
      setCalculatingProration(false);
    }
  };

  const handleConfirmTierChange = async () => {
    if (!selectedTier || !proratedAmount) return;
    setConfirming(true);
    try {
      const isUpgrade = (selectedTier.sortOrder ?? 0) > currentTierSortOrder;
      const response = await axiosInstance.post('/merchant/billing/initiate-tier-change', {
        newTier: selectedTier.tier,
        action: isUpgrade ? 'upgrade' : 'downgrade',
        amount: proratedAmount.amount,
        currency: proratedAmount.currency || 'NGN',
        requiresPayment: proratedAmount.requiresPayment,
        isFreeTarget: proratedAmount.isFreeTarget,
      });
      if (response.data?.success) {
        if (response.data.noPaymentRequired) {
          await fetchData();
          setShowProrationModal(false);
          setSelectedTier(null);
          setProratedAmount(null);
          toast.success(proratedAmount.explanation || 'Plan changed successfully.');
        } else if (response.data.authorizationUrl) {
          window.location.href = response.data.authorizationUrl;
        } else {
          throw new Error('Invalid response from server');
        }
      }
    } catch (err) {
      console.error('Tier change error:', err);
      toast.error(err.response?.data?.error || 'Failed to process tier change. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const closeProrationModal = () => {
    setShowProrationModal(false);
    setSelectedTier(null);
    setProratedAmount(null);
  };

  const renewalDate = currentSubscription?.currentPeriodEnd
    ? new Date(currentSubscription.currentPeriodEnd).toLocaleDateString('en-NG', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : null;
  const daysUntilRenewal = currentSubscription?.currentPeriodEnd
    ? Math.max(0, Math.ceil(
        (new Date(currentSubscription.currentPeriodEnd) - new Date()) / 86400000
      ))
    : null;

  const isActive = currentSubscription?.status === 'ACTIVE';

  return (
    <Shell>
      <div className="up-page">

        {/* ── Header ─────────────────────────────────────────── */}
        <header className="up-header">
          <div className="up-header-text">
            <h1 className="up-title">Subscription plans</h1>
            <p className="up-subtitle">Choose the plan that best fits your business.</p>
          </div>
          <div className="up-current-pill">
            <span className="up-current-label">Current plan</span>
            <span className="up-pill up-pill-brand">
              {currentTierData?.name || currentTier}
            </span>
            {currentSubscription?.status && (
              <span className={`up-pill ${isActive ? 'up-pill-success' : 'up-pill-warn'}`}>
                <span className="up-pill-dot" /> {currentSubscription.status}
              </span>
            )}
          </div>
        </header>

        {/* ── Slim current-subscription banner (replaces the 4-tile card) ── */}
        {currentSubscription && (
          <div className="up-current-banner">
            <span className="up-current-banner-text">
              You're on <strong>{currentTierData?.name || currentTier}</strong>
              {renewalDate && daysUntilRenewal !== null && daysUntilRenewal > 0
                ? ` · Renews in ${daysUntilRenewal} day${daysUntilRenewal === 1 ? '' : 's'} on ${renewalDate}`
                : ''}
            </span>
          </div>
        )}

        {/* ── Billing period toggle ──────────────────────────── */}
        <div className="up-billing-row">
          <div className="up-toggle">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                className={`up-toggle-btn${selectedPeriod === p.value ? ' is-active' : ''}`}
                onClick={() => setSelectedPeriod(p.value)}
              >
                {p.label}
                {p.value === 'YEARLY' && <span className="up-save">Save 17%</span>}
              </button>
            ))}
          </div>
        </div>

        {/* ── Plans grid ─────────────────────────────────────── */}
        {loading ? (
          <div className="up-loading"><Loader2 size={20} className="up-spin" /> Loading plans…</div>
        ) : (
          <div className="up-plans">
            {tiers
              .slice()
              .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
              .map((tier) => {
                const price = getPrice(tier, selectedPeriod);
                const isCurrent = isCurrentPlan(tier.tier);
                const tierKey = normalizeTierKey(tier.tier);
                const badge = getPlanBadge(tierKey, isCurrent);

                // Prefer curated marketing copy; fall back to top items from
                // the backend's tier.features (name-only) for admin-created
                // tiers that don't have a PLAN_HIGHLIGHTS entry — otherwise
                // those cards render with an empty feature list.
                let highlights = PLAN_HIGHLIGHTS[tierKey];
                if (!highlights || highlights.length === 0) {
                  const backendFeatures = Array.isArray(tier.features) ? tier.features : [];
                  highlights = backendFeatures.slice(0, 4).map((f) =>
                    typeof f === 'string' ? f : (f.name || f.id || 'Feature')
                  );
                }

                // Card gets a "highlighted" visual only for the popular tier
                // (single card at a time — the row shouldn't fight for eyes).
                const isHighlighted = !isCurrent && badge?.variant === 'popular';

                return (
                  <div
                    key={tier.tier}
                    className={`up-plan${isCurrent ? ' is-current' : ''}${isHighlighted ? ' is-popular' : ''}`}
                  >
                    {badge && (
                      <span className={`up-plan-tag up-plan-tag-${badge.variant}`}>
                        {badge.text}
                      </span>
                    )}

                    <h3 className="up-plan-name">{tier.name || tier.tier}</h3>

                    <div className="up-plan-price">
                      <span className="up-plan-amount">{formatPrice(price)}</span>
                      <span className="up-plan-period">
                        {price === 0 ? 'forever' : `/${selectedPeriod === 'YEARLY' ? 'year' : 'month'}`}
                      </span>
                    </div>

                    <button
                      type="button"
                      className={`up-btn up-plan-cta ${
                        isCurrent ? 'up-btn-secondary'
                        : isHighlighted ? 'up-btn-primary'
                        : 'up-btn-secondary'
                      }`}
                      onClick={() => !isCurrent && handleSelectTier(tier)}
                      disabled={isCurrent || processingTier !== null}
                    >
                      {getButtonText(tier)}
                    </button>

                    <ul className="up-plan-features">
                      {highlights.map((line, i) => (
                        <li key={i}>
                          <Check size={14} className="up-feature-check" />
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
          </div>
        )}

        {/* ── Proration confirmation modal — legacy flow, gated ── */}
        {!UPGRADE_CHECKOUT_V2 && <Modal
          isOpen={showProrationModal}
          onClose={closeProrationModal}
          title={selectedTier
            ? `${isUpgradeTier(selectedTier) ? 'Upgrade' : 'Downgrade'} to ${selectedTier.name || selectedTier.tier}`
            : 'Confirm change'}
          size="md"
          padded={false}
          footer={
            <>
              <button
                type="button"
                className="up-btn up-btn-secondary"
                onClick={closeProrationModal}
                disabled={confirming}
              >
                Cancel
              </button>
              <button
                type="button"
                className="up-btn up-btn-primary"
                onClick={handleConfirmTierChange}
                disabled={calculatingProration || !proratedAmount || confirming}
              >
                {confirming ? <Loader2 size={14} className="up-spin" /> : null}
                {confirming ? 'Processing…'
                  : proratedAmount?.isFreeTarget ? 'Confirm downgrade'
                  : proratedAmount?.requiresPayment ? 'Proceed to payment'
                  : 'Confirm change'}
              </button>
            </>
          }
        >
          <div className="up-proration">
            {calculatingProration ? (
              <div className="up-loading">
                <Loader2 size={18} className="up-spin" /> Calculating pricing…
              </div>
            ) : proratedAmount ? (
              <div className="up-proration-body">
                {proratedAmount.requiresPayment ? (
                  <>
                    <div className="up-proration-amount">
                      <span className="up-proration-label">Amount to pay</span>
                      <span className="up-proration-value">
                        {formatPrice(proratedAmount.amount, proratedAmount.currency)}
                      </span>
                    </div>
                    {proratedAmount.daysRemaining > 0 && (
                      <div className="up-proration-row">
                        <span>Days remaining in current period</span>
                        <strong>{proratedAmount.daysRemaining} days</strong>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="up-proration-amount up-proration-amount-success">
                    <span className="up-proration-label">No payment required</span>
                    {proratedAmount.creditAmount > 0 && (
                      <span className="up-proration-credit">
                        Credit of {formatPrice(proratedAmount.creditAmount, proratedAmount.currency)} will apply to your next billing period.
                      </span>
                    )}
                  </div>
                )}

                {proratedAmount.explanation && (
                  <div className="up-proration-note">
                    <Sparkles size={14} />
                    <span>{proratedAmount.explanation}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="up-proration-error">
                Failed to calculate pricing. Please try again.
              </div>
            )}
          </div>
        </Modal>}

      </div>
    </Shell>
  );
}
